import { ModifierItem } from "src/interfaces/modifier.interface";

const EMPTY = "\u2013";

export interface ModifierItemsTableProps {
  items: ModifierItem[];
  onEdit: (item: ModifierItem) => void;
  onDelete: (correlationId: string) => void;
}

const TABLE_MIN_WIDTH = 520;
const stickyHeaderClass = "sticky top-0 z-10 bg-[#f1f5f9]";

const ModifierItemsTable = ({ items, onEdit, onDelete }: ModifierItemsTableProps) => {
  return (
    <div className="w-full h-full flex flex-col min-h-0 bg-white rounded-xl border border-[#F1F1F2] shadow-[0_4px_12px_rgba(0,0,0,0.04)] overflow-hidden">
      <div className="flex-1 min-h-0 overflow-auto">
        <table className="w-full text-left border-collapse table-auto" style={{ minWidth: TABLE_MIN_WIDTH }}>
          <thead>
            <tr className="text-[12px] font-semibold uppercase tracking-wide text-[#374151] border-b border-[#e2e8f0]">
              <th className={`py-3 pl-4 pr-2 align-middle w-12 whitespace-nowrap ${stickyHeaderClass}`}>#</th>
              <th className={`py-3 px-3 align-middle whitespace-nowrap ${stickyHeaderClass}`} style={{ minWidth: 220 }}>Name</th>
              <th className={`py-3 px-3 align-middle whitespace-nowrap text-center ${stickyHeaderClass}`} style={{ minWidth: 100 }}>Options</th>
              <th className={`py-3 pl-2 pr-4 align-middle text-right whitespace-nowrap ${stickyHeaderClass}`} style={{ width: 120 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => {
              const optionCount = item.modifierItemOptions?.length ?? 0;
              const isInactive = item.active === false;
              return (
                <tr
                  key={item.correlationId}
                  className={`border-t border-[#f1f5f9] text-sm text-[#111827] hover:bg-[#f3f4f6] cursor-pointer transition-colors group ${
                    isInactive ? "opacity-[0.92] bg-[#fafafa]" : ""
                  }`}
                  onClick={() => onEdit(item)}
                >
                  <td className="py-3 pl-4 pr-2 align-middle font-medium w-12">{index + 1}</td>
                  <td className="py-3 px-3 align-middle font-medium" style={{ minWidth: 220 }}>
                    <div className="flex flex-col gap-1.5 min-w-0">
                      <span className="truncate">
                        {item.name ? item.name : <span className="text-[#9CA3AF] font-normal">{EMPTY}</span>}
                      </span>
                      {isInactive ? (
                        <span className="inline-flex items-center w-fit text-[11px] text-[#92400E] font-medium rounded-full border border-[#FCD34D] bg-[#FFFBEB] py-0.5 px-2">
                          Inactive
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="py-3 px-3 align-middle text-center tabular-nums font-medium" style={{ minWidth: 100 }}>
                    {optionCount > 0 ? optionCount : <span className="text-[#9CA3AF] font-normal">{EMPTY}</span>}
                  </td>
                  <td className="py-3 pl-2 pr-4 align-middle text-right" style={{ width: 120 }} onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-0.5">
                      <button
                        type="button"
                        className="p-2 rounded-full text-[#6B7280] opacity-80 hover:opacity-100 hover:bg-[#f3f4f6] hover:text-[#111827] transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(item);
                        }}
                        aria-label="Edit"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="p-2 rounded-full text-[#6B7280] opacity-80 hover:opacity-100 hover:bg-red-50 hover:text-[#DC2626] transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (item.correlationId) onDelete(item.correlationId);
                        }}
                        aria-label="Delete"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                          <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.518.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ModifierItemsTable;
